import type { CSSProperties } from 'react';
import styles from './DocumentContent.module.css';

interface IDocumentContentProps {
  isPdf: boolean;
  /** Laid out but not shown until the page is ready: the element has to be in
   *  the tree to load and to measure, and a half-painted page must not flash. */
  shown: boolean;
  /** The pdf page's canvas — a callback ref: the renderer follows the element,
   *  which the mode switch remounts. */
  canvasRef: (node: HTMLCanvasElement | null) => void;
  imageUrl: string | null;
  docName: string;
  imageZoomStyle: CSSProperties | undefined;
  onImageLoad: (naturalWidth: number, naturalHeight: number, url: string) => void;
}

/**
 * The document itself — a PDF page's canvas or the image. The same element
 * whether it is being read or drawn on, so switching modes never re-decodes
 * the file.
 */
export function DocumentContent({
  isPdf, shown, canvasRef, imageUrl, docName, imageZoomStyle, onImageLoad,
}: IDocumentContentProps) {
  const className = shown ? styles.content : `${styles.content} ${styles.pending}`;
  if (isPdf) return <canvas ref={canvasRef} className={className} />;
  if (!imageUrl) return null;
  return (
    <img
      src={imageUrl}
      alt={docName}
      className={className}
      style={imageZoomStyle}
      draggable={false}
      onLoad={(e) => onImageLoad(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight, imageUrl)}
    />
  );
}
